/** Demo Mode only — backs the Controls Dashboard (Stage 7 Compliance & Controls).
 * Read-only KPI/summary data; there is no CRUD against this file. */

export type ControlAreaName =
  | 'Stock'
  | 'Ordering'
  | 'Labour'
  | 'Food Safety'
  | 'Allergens'
  | 'Nutrition'
  | 'Waste'
  | 'Quality'
  | 'Pricing'
  | 'Hospitality'
  | 'Events'
  | 'Customer Experience';

export interface ControlAreaExceptionCount {
  controlArea: ControlAreaName;
  openCount: number;
  trend: 'up' | 'down' | 'flat';
}

export interface HighRiskSite {
  siteId: string;
  siteName: string;
  openIssues: number;
  overdueActions: number;
  lastAuditScore: number;
  riskReason: string;
}

export interface RepeatedFailure {
  id: string;
  controlArea: ControlAreaName;
  description: string;
  occurrences: number;
  sitesAffected: number;
  lastOccurredOn: string;
}

export interface NextBestAction {
  id: string;
  title: string;
  controlArea: ControlAreaName;
  priority: 'Low' | 'Medium' | 'High' | 'Critical';
  reason: string;
}

export const controlAreaExceptionCounts: ControlAreaExceptionCount[] = [
  { controlArea: 'Food Safety', openCount: 14, trend: 'up' },
  { controlArea: 'Allergens', openCount: 6, trend: 'flat' },
  { controlArea: 'Waste', openCount: 9, trend: 'down' },
  { controlArea: 'Stock', openCount: 11, trend: 'up' },
  { controlArea: 'Labour', openCount: 7, trend: 'flat' },
  { controlArea: 'Pricing', openCount: 4, trend: 'down' },
  { controlArea: 'Hospitality', openCount: 3, trend: 'flat' },
  { controlArea: 'Customer Experience', openCount: 8, trend: 'up' },
];

export const highRiskSites: HighRiskSite[] = [
  { siteId: 'STR-1042', siteName: 'Riverside Campus Kitchen', openIssues: 9, overdueActions: 4, lastAuditScore: 62, riskReason: 'Repeated food safety failures + overdue action items' },
  { siteId: 'STR-2078', siteName: 'Northgate Distribution Hub', openIssues: 6, overdueActions: 3, lastAuditScore: 71, riskReason: 'Stock variance outside tolerance three periods running' },
  { siteId: 'STR-3311', siteName: 'Harbourview Events Venue', openIssues: 5, overdueActions: 2, lastAuditScore: 68, riskReason: 'Hospitality/event readiness checks missed' },
  { siteId: 'STR-4456', siteName: 'Meadowbrook Care Catering', openIssues: 4, overdueActions: 1, lastAuditScore: 75, riskReason: 'Allergen labelling gaps on two menu items' },
];

export const repeatedFailures: RepeatedFailure[] = [
  { id: 'RF-001', controlArea: 'Food Safety', description: 'Fridge temperature check failed', occurrences: 5, sitesAffected: 3, lastOccurredOn: '2026-07-05' },
  { id: 'RF-002', controlArea: 'Waste', description: 'Waste recorded over threshold', occurrences: 4, sitesAffected: 2, lastOccurredOn: '2026-07-06' },
  { id: 'RF-003', controlArea: 'Stock', description: 'Closing stock variance outside tolerance', occurrences: 6, sitesAffected: 4, lastOccurredOn: '2026-07-08' },
  { id: 'RF-004', controlArea: 'Labour', description: 'Certification expired before shift', occurrences: 3, sitesAffected: 3, lastOccurredOn: '2026-07-04' },
];

export const nextBestActions: NextBestAction[] = [
  { id: 'NBA-001', title: 'Escalate Riverside Campus Kitchen food safety findings to area manager', controlArea: 'Food Safety', priority: 'Critical', reason: '3 overdue action items, repeat failure pattern' },
  { id: 'NBA-002', title: 'Review stock variance root cause at Northgate Distribution Hub', controlArea: 'Stock', priority: 'High', reason: 'Variance outside tolerance for 3 consecutive periods' },
  { id: 'NBA-003', title: 'Re-issue allergen labels for affected menu items', controlArea: 'Allergens', priority: 'High', reason: 'Labelling gap flagged by 2 sites' },
  { id: 'NBA-004', title: 'Confirm hospitality setup checklist completion for upcoming events', controlArea: 'Hospitality', priority: 'Medium', reason: 'Missed setup tasks trending up' },
];

export interface ControlsDashboardSummary {
  failedControls: number;
  overdueChecks: number;
  missedOperationalTasks: number;
  openAuditFindings: number;
  openActionItems: number;
  highRiskSiteCount: number;
  repeatedFailureCount: number;
}

export const controlsDashboardSummary: ControlsDashboardSummary = {
  failedControls: 27,
  overdueChecks: 18,
  missedOperationalTasks: 12,
  openAuditFindings: 15,
  openActionItems: 23,
  highRiskSiteCount: highRiskSites.length,
  repeatedFailureCount: repeatedFailures.length,
};
